import { getAPI, patchAPI, postAPI } from "../../../api/Networking";
import { PaginatedAPIResponse } from "../../../models/NetworkResponse";
import {
  asBool,
  CreateProductInput,
  ProductCategoryApi,
  ProductDetailApi,
  ProductImageApi,
  ProductListApi,
  ProductListFilters,
  ProductPrintingTypeApi,
  ProductVariantApi,
  ProductVariantImageApi,
  ProductVendorApi,
  UpdateCategoriesInput,
  UpdateImagesInput,
  UpdatePrintingTypesInput,
  UpdateProductInput,
  UpdateSeoInput,
  UpdateSourcingInput,
  UpdateVariantImagesInput,
  UpdateVariantsInput,
  UpdateVendorsInput,
  UpdatePodInput,
  UpdatePodVariantsInput,
  ProductPodApi,
  ProductPodVariantApi,
  normalizeProductListSortBy,
  UrgentSourcingProductApi,
} from "../../../models/CatalogProducts/types";
import {
  ProductActivityLog,
  ProductActivityLogChangeDetail,
  ProductActivityLogFilters,
} from "../../../models/CatalogProducts/ProductActivityLog";

const BASE =
  process.env.REACT_APP_CATALOG_PRODUCTS_API_BASE?.trim() ||
  "/v1/catalog/products";

function pickStr(v: unknown): string | null {
  if (v == null) return null;
  const s = typeof v === "string" ? v.trim() : String(v).trim();
  return s.length ? s : null;
}

function pickNum(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function unwrapResult(raw: unknown): unknown {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    if (
      !("list" in o) &&
      !("items" in o) &&
      o.result &&
      typeof o.result === "object"
    ) {
      return o.result;
    }
  }
  return raw;
}

function assertSuccess(response: {
  success?: boolean;
  message?: string;
}): void {
  if (response && response.success === false) {
    throw {
      response: { data: { message: response.message || "Request failed." } },
      message: response.message || "Request failed.",
    };
  }
}

function normalizeListItem(raw: unknown): ProductListApi {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    id: Number(r.id) || 0,
    name: pickStr(r.name) ?? "",
    category_id: Number(r.category_id) || 0,
    category_name: pickStr(r.category_name),
    preferred_vendor_id: pickNum(r.preferred_vendor_id),
    preferred_vendor_name: pickStr(r.preferred_vendor_name),
    sku: pickStr(r.sku),
    cost_price: Number(r.cost_price) || 0,
    selling_price: Number(r.selling_price) || 0,
    hsn_code: pickStr(r.hsn_code),
    gst_percentage: pickNum(r.gst_percentage),
    display: asBool(r.display),
    active: asBool(r.active),
    default_image_url: pickStr(r.default_image_url),
    created_by: pickNum(r.created_by),
    created_by_name: pickStr(r.created_by_name),
    last_modified_by: pickNum(r.last_modified_by),
    last_modified_by_name: pickStr(r.last_modified_by_name),
    created_date: pickStr(r.created_date) ?? (r.created_date as string) ?? null,
    modified_date:
      pickStr(r.modified_date) ?? (r.modified_date as string) ?? null,
  };
}

function normalizeCategory(raw: unknown): ProductCategoryApi {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    category_id: Number(r.category_id) || 0,
    category_name: pickStr(r.category_name) ?? "",
    is_primary: asBool(r.is_primary),
    active: asBool(r.active ?? 1),
    markup: pickNum(r.markup),
  };
}

function normalizeVendor(raw: unknown): ProductVendorApi {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    vendor_id: Number(r.vendor_id) || 0,
    vendor_name: pickStr(r.vendor_name) ?? "",
    is_preferred: asBool(r.is_preferred),
    active: asBool(r.active ?? 1),
  };
}

function normalizeImage(raw: unknown): ProductImageApi {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    id: Number(r.id) || 0,
    image_url: pickStr(r.image_url) ?? "",
    preferred: asBool(r.preferred),
    is_default: asBool(r.is_default),
    active: asBool(r.active ?? 1),
  };
}

function normalizeVariantImage(raw: unknown): ProductVariantImageApi {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    id: Number(r.id) || 0,
    image_url: pickStr(r.image_url) ?? "",
    active: asBool(r.active ?? 1),
  };
}

function normalizeVariant(raw: unknown): ProductVariantApi {
  const r = (raw ?? {}) as Record<string, unknown>;
  const imagesRaw = r.images ?? r.image_urls;
  const detailIdsRaw = r.option_detail_ids ?? r.optionDetailIds;
  return {
    id: Number(r.id) || 0,
    options_id: Number(r.options_id) || 0,
    option_name: pickStr(r.option_name),
    name: pickStr(r.name) ?? "",
    title: pickStr(r.title),
    hex_code: pickStr(r.hex_code),
    cost_price: pickNum(r.cost_price),
    selling_price: pickNum(r.selling_price),
    sku: pickStr(r.sku),
    active: asBool(r.active ?? 1),
    option_detail_ids: Array.isArray(detailIdsRaw)
      ? detailIdsRaw
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0)
      : undefined,
    images: Array.isArray(imagesRaw)
      ? imagesRaw.map(normalizeVariantImage).filter((img) => img.image_url)
      : undefined,
  };
}

function normalizePrintingType(raw: unknown): ProductPrintingTypeApi {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    id: Number(r.id) || 0,
    printing_type_id: Number(r.printing_type_id) || 0,
    printing_type_name: pickStr(r.printing_type_name),
    active: asBool(r.active ?? 1),
  };
}

function normalizeUrgentSourcingDetail(raw: unknown): UrgentSourcingProductApi {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    id: Number(r.id) || 0,
    name: pickStr(r.name) ?? "",
    timeline: Number(r.timeline) || 0,
    price_without_tax: Number(r.price_without_tax) || 0,
    fifty_quantity_price_without_tax:
      Number(r.fifty_quantity_price_without_tax) || 0,
    category_id: Number(r.category_id) || 0,
    gst: Number(r.gst) || 0,
    price_with_gst: Number(r.price_with_gst) || 0,
    selling_price: Number(r.selling_price) || 0,
    hsn: pickStr(r.hsn) ?? "",
    description: pickStr(r.description) ?? "",
    vendor_name: pickStr(r.vendor_name) ?? "",
    vendor_phone_number: pickStr(r.vendor_phone_number) ?? "",
    added_by: Number(r.added_by) || 0,
    is_admin_product: asBool(r.is_admin_product),
    admin_product_id: Number(r.admin_product_id) || 0,
    active: asBool(r.active ?? 1),
    modified_date: (r.modified_date as Date | string | null) ?? null,
    created_date: (r.created_date as Date | string | null) ?? null,
  };
}

function normalizeActivityLogChangeDetail(
  raw: unknown,
): ProductActivityLogChangeDetail {
  const r = (raw ?? {}) as Record<string, unknown>;
  const pickPrimitive = (v: unknown): string | number | boolean | null => {
    if (v === null || v === undefined) return null;
    if (typeof v === "boolean" || typeof v === "number") return v;
    return String(v);
  };
  return {
    field: pickStr(r.field) ?? "",
    label: pickStr(r.label) ?? pickStr(r.field) ?? "",
    from: pickPrimitive(r.from),
    to: pickPrimitive(r.to),
  };
}

function normalizeActivityLog(raw: unknown): ProductActivityLog {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    id: Number(r.id) || 0,
    product_id: Number(r.product_id) || 0,
    product_name: pickStr(r.product_name),
    action: pickStr(r.action) ?? "",
    action_label: pickStr(r.action_label) ?? "",
    entity_type: pickStr(r.entity_type) ?? "",
    entity_type_label: pickStr(r.entity_type_label) ?? "",
    summary: pickStr(r.summary) ?? "",
    admin_id: pickNum(r.admin_id),
    admin_name: pickStr(r.admin_name),
    created_date:
      pickStr(r.created_date) ?? (r.created_date as string) ?? "",
    change_details: Array.isArray(r.change_details)
      ? r.change_details.map(normalizeActivityLogChangeDetail)
      : [],
    changes:
      r.changes && typeof r.changes === "object"
        ? (r.changes as Record<string, unknown>)
        : null,
  };
}

function normalizeActivityLogsResponse(
  params: ProductActivityLogFilters,
  raw: unknown,
): PaginatedAPIResponse<ProductActivityLog> {
  const r = (raw ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(r.list) ? r.list : [];
  return {
    limit: Number(r.limit ?? params.limit ?? 20) || 20,
    page_no: Number(r.page_no ?? params.page_no ?? 1) || 1,
    total_count: Number(r.total_count ?? rows.length) || 0,
    total_pages: Number(r.total_pages ?? 1) || 1,
    list: rows.map(normalizeActivityLog),
  };
}

function normalizeDetail(raw: unknown): ProductDetailApi {
  const base = normalizeListItem(raw);
  const r = (raw ?? {}) as Record<string, unknown>;
  const arr = (key: string): unknown[] =>
    Array.isArray(r[key]) ? (r[key] as unknown[]) : [];
  return {
    ...base,
    zoho_id: pickStr(r.zoho_id),
    vendor_code: pickStr(r.vendor_code),
    brand_name: pickStr(r.brand_name),
    dimensions: pickStr(r.dimensions),
    size_chart_url: pickStr(r.size_chart_url),
    sizes: pickStr(r.sizes),
    capacity: pickStr(r.capacity),
    desc: pickStr(r.desc),
    colors: pickStr(r.colors),
    priority: Number(r.priority) || 0,
    fast_procurement: asBool(r.fast_procurement),
    preferred_printing_type_id: pickNum(r.preferred_printing_type_id),
    print_on_demand: asBool(r.print_on_demand),
    is_best_selling: asBool(r.is_best_selling),
    length: pickNum(r.length),
    breadth: pickNum(r.breadth),
    height: pickNum(r.height),
    weight: pickNum(r.weight),
    seo_title: pickStr(r.seo_title),
    seo_description: pickStr(r.seo_description),
    categories: arr("categories").map(normalizeCategory),
    vendors: arr("vendors").map(normalizeVendor),
    images: arr("images").map(normalizeImage),
    variants: arr("variants").map(normalizeVariant),
    printing_types: arr("printing_types").map(normalizePrintingType),
    urgent_sourcing_details: normalizeUrgentSourcingDetail(
      r.urgent_sourcing_details,
    ),
  };
}

export default class CatalogProductsAPI {
  private normalizeListResponse(
    params: ProductListFilters,
    raw: unknown,
  ): PaginatedAPIResponse<ProductListApi> {
    if (Array.isArray(raw)) {
      const list = raw.map(normalizeListItem);
      return {
        list,
        limit: params.limit ?? list.length,
        page_no: params.page_no ?? 1,
        total_count: list.length,
        total_pages: 1,
      };
    }

    const r = (raw ?? {}) as Record<string, unknown>;
    const rows = r.list ?? r.items ?? r.data;
    const list = Array.isArray(rows) ? rows.map(normalizeListItem) : [];

    return {
      limit: Number(r.limit ?? params.limit ?? 20) || 20,
      page_no: Number(r.page_no ?? params.page_no ?? 1) || 1,
      total_count: Number(r.total_count ?? list.length) || 0,
      total_pages: Number(r.total_pages ?? 1) || 1,
      list,
    };
  }

  listProducts = async (
    params: ProductListFilters,
  ): Promise<PaginatedAPIResponse<ProductListApi>> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page_no", String(params.page_no ?? 1));
      queryParams.append("limit", String(params.limit ?? 20));
      queryParams.append("sort_by", normalizeProductListSortBy(params.sort_by));
      if (params.sort_type != null) {
        queryParams.append("sort_type", String(params.sort_type));
      }
      const term = params.search?.trim();
      if (term) queryParams.append("search", term);
      if (params.id != null) queryParams.append("id", String(params.id));
      if (params.category_id != null && params.category_id.length > 0) {
        queryParams.append("category_id", params.category_id.join(","));
      }
      if (params.preferred_vendor_id != null) {
        queryParams.append(
          "preferred_vendor_id",
          String(params.preferred_vendor_id),
        );
      }
      if (params.active !== undefined) {
        queryParams.append("active", String(params.active));
      }
      if (params.display !== undefined) {
        queryParams.append("display", String(params.display));
      }
      if (params.min_selling_price != null) {
        queryParams.append(
          "min_selling_price",
          String(params.min_selling_price),
        );
      }
      if (params.max_selling_price != null) {
        queryParams.append(
          "max_selling_price",
          String(params.max_selling_price),
        );
      }

      const response = await getAPI(`${BASE}?${queryParams.toString()}`);
      assertSuccess(response);
      return this.normalizeListResponse(params, unwrapResult(response?.result));
    } catch (error) {
      throw error;
    }
  };

  getProductById = async (id: number): Promise<ProductDetailApi> => {
    try {
      const response = await getAPI(`${BASE}/${id}`);
      assertSuccess(response);
      return normalizeDetail(unwrapResult(response.result));
    } catch (error) {
      throw error;
    }
  };

  getActivityLogs = async (
    id: number,
    params: ProductActivityLogFilters,
  ): Promise<PaginatedAPIResponse<ProductActivityLog>> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page_no", String(params.page_no ?? 1));
      queryParams.append("limit", String(params.limit ?? 20));

      const response = await getAPI(
        `${BASE}/${id}/activity-logs?${queryParams.toString()}`,
      );
      assertSuccess(response);
      return normalizeActivityLogsResponse(
        params,
        unwrapResult(response.result),
      );
    } catch (error) {
      throw error;
    }
  };

  getPurchaseRequestInsights = async (productId: number) => {
    try {
      const response = await getAPI(`/v5/products/insights/id/${productId}`);
      return response.result ?? [];
    } catch (error) {
      throw error;
    }
  };

  createProduct = async (
    body: CreateProductInput,
  ): Promise<ProductDetailApi> => {
    try {
      const response = await postAPI(BASE, body);
      assertSuccess(response);
      return normalizeDetail(unwrapResult(response.result));
    } catch (error) {
      throw error;
    }
  };

  updateProduct = async (
    id: number,
    body: UpdateProductInput,
  ): Promise<ProductDetailApi> => {
    try {
      const response = await patchAPI(`${BASE}/${id}`, body);
      assertSuccess(response);
      return normalizeDetail(unwrapResult(response.result));
    } catch (error) {
      throw error;
    }
  };

  updateSourcing = async (
    id: number,
    body: UpdateSourcingInput,
  ): Promise<ProductDetailApi> => {
    try {
      const response = await patchAPI(`${BASE}/${id}/sourcing`, body);
      assertSuccess(response);
      return normalizeDetail(unwrapResult(response.result));
    } catch (error) {
      throw error;
    }
  };

  updateSeo = async (
    id: number,
    body: UpdateSeoInput,
  ): Promise<ProductDetailApi> => {
    try {
      const response = await patchAPI(`${BASE}/${id}/seo`, body);
      assertSuccess(response);
      return normalizeDetail(unwrapResult(response.result));
    } catch (error) {
      throw error;
    }
  };

  updateImages = async (
    id: number,
    body: UpdateImagesInput,
  ): Promise<ProductDetailApi> => {
    try {
      const response = await patchAPI(`${BASE}/${id}/images`, body);
      assertSuccess(response);
      return normalizeDetail(unwrapResult(response.result));
    } catch (error) {
      throw error;
    }
  };

  fetchVariants = async (productId: number): Promise<ProductVariantApi[]> => {
    try {
      const response = await getAPI(`${BASE}/${productId}/variants`);
      assertSuccess(response);
      const raw = unwrapResult(response.result);
      const rows = Array.isArray(raw)
        ? raw
        : raw &&
            typeof raw === "object" &&
            Array.isArray((raw as Record<string, unknown>).list)
          ? ((raw as Record<string, unknown>).list as unknown[])
          : raw &&
              typeof raw === "object" &&
              Array.isArray((raw as Record<string, unknown>).variants)
            ? ((raw as Record<string, unknown>).variants as unknown[])
            : [];
      return rows.map(normalizeVariant);
    } catch (error) {
      throw error;
    }
  };

  updateVariants = async (
    id: number,
    body: UpdateVariantsInput,
  ): Promise<ProductDetailApi> => {
    try {
      const response = await patchAPI(`${BASE}/${id}/variants`, body);
      assertSuccess(response);
      return normalizeDetail(unwrapResult(response.result));
    } catch (error) {
      throw error;
    }
  };

  updateVariantImages = async (
    productId: number,
    variantId: number,
    body: UpdateVariantImagesInput,
  ): Promise<{
    detail?: ProductDetailApi;
    variantImages?: ProductVariantImageApi[];
  }> => {
    try {
      const response = await patchAPI(
        `${BASE}/${productId}/variants/${variantId}/images`,
        body,
      );
      assertSuccess(response);
      const raw = unwrapResult(response.result);

      if (Array.isArray(raw)) {
        return { variantImages: raw.map(normalizeVariantImage) };
      }

      if (raw && typeof raw === "object") {
        const o = raw as Record<string, unknown>;
        if (Array.isArray(o.variants) || ("category_id" in o && "name" in o)) {
          return { detail: normalizeDetail(raw) };
        }
        if (Array.isArray(o.images) || Array.isArray(o.image_urls)) {
          const variant = normalizeVariant(raw);
          return {
            variantImages:
              variant.images ??
              (Array.isArray(o.images)
                ? o.images.map(normalizeVariantImage)
                : []),
          };
        }
      }

      return {};
    } catch (error) {
      throw error;
    }
  };

  updateCategories = async (
    id: number,
    body: UpdateCategoriesInput,
  ): Promise<ProductDetailApi> => {
    try {
      const response = await patchAPI(`${BASE}/${id}/categories`, body);
      assertSuccess(response);
      return normalizeDetail(unwrapResult(response.result));
    } catch (error) {
      throw error;
    }
  };

  updateVendors = async (
    id: number,
    body: UpdateVendorsInput,
  ): Promise<ProductDetailApi> => {
    try {
      const response = await patchAPI(`${BASE}/${id}/vendors`, body);
      assertSuccess(response);
      return normalizeDetail(unwrapResult(response.result));
    } catch (error) {
      throw error;
    }
  };

  updatePrintingTypes = async (
    id: number,
    body: UpdatePrintingTypesInput,
  ): Promise<ProductDetailApi> => {
    try {
      const response = await patchAPI(`${BASE}/${id}/printing-types`, body);
      assertSuccess(response);
      return normalizeDetail(unwrapResult(response.result));
    } catch (error) {
      throw error;
    }
  };

  toggleDisplay = async (
    id: number,
  ): Promise<{ id: number; display: boolean }> => {
    try {
      const response = await patchAPI(`${BASE}/${id}/toggle-display`, {});
      assertSuccess(response);
      const r = (unwrapResult(response.result) ?? {}) as Record<
        string,
        unknown
      >;
      return {
        id: Number(r.id ?? id),
        display: asBool(r.display),
      };
    } catch (error) {
      throw error;
    }
  };

  toggleActive = async (
    id: number,
  ): Promise<{ id: number; active: boolean }> => {
    try {
      const response = await patchAPI(`${BASE}/${id}/toggle-active`, {});
      assertSuccess(response);
      const r = (unwrapResult(response.result) ?? {}) as Record<
        string,
        unknown
      >;
      return {
        id: Number(r.id ?? id),
        active: asBool(r.active),
      };
    } catch (error) {
      throw error;
    }
  };

  getPod = async (id: number): Promise<ProductPodApi> => {
    try {
      const response = await getAPI(`${BASE}/${id}/pod`);
      assertSuccess(response);
      return normalizePod(unwrapResult(response.result));
    } catch (error) {
      throw error;
    }
  };

  updatePod = async (
    id: number,
    body: UpdatePodInput,
  ): Promise<ProductPodApi> => {
    try {
      const response = await patchAPI(`${BASE}/${id}/pod`, body);
      assertSuccess(response);
      return normalizePod(unwrapResult(response.result));
    } catch (error) {
      throw error;
    }
  };

  updatePodVariants = async (
    id: number,
    body: UpdatePodVariantsInput,
  ): Promise<ProductPodApi> => {
    try {
      const response = await patchAPI(`${BASE}/${id}/pod/variants`, body);
      assertSuccess(response);
      return normalizePod(unwrapResult(response.result));
    } catch (error) {
      throw error;
    }
  };
}

function normalizePodVariant(raw: unknown): ProductPodVariantApi {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<
    string,
    unknown
  >;
  const sku = pickStr(o.sku) ?? pickStr(o.variant_sku) ?? "";
  return {
    id: Number(o.id ?? 0),
    product_id: Number(o.product_id ?? 0),
    variant_id: pickNum(o.variant_id),
    sku,
    variant_sku: pickStr(o.variant_sku) ?? sku,
    active: asBool(o.active),
    variant_name: pickStr(o.variant_name),
    color: pickStr(o.color),
    size: pickStr(o.size),
    created_by_admin_id: pickNum(o.created_by_admin_id),
    updated_by_admin_id: pickNum(o.updated_by_admin_id),
    created_date: (o.created_date as Date | string | null) ?? null,
    modified_date: (o.modified_date as Date | string | null) ?? null,
  };
}

function normalizePod(raw: unknown): ProductPodApi {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<
    string,
    unknown
  >;
  const variantsRaw = Array.isArray(o.variants) ? o.variants : [];
  const podPrice =
    pickNum(o.pod_selling_price) ?? pickNum(o.swageazy_selling_price) ?? 0;
  const printOnDemand =
    o.print_on_demand !== undefined
      ? asBool(o.print_on_demand)
      : o.is_catalogue_product !== undefined
        ? asBool(o.is_catalogue_product)
        : asBool(o.active);
  return {
    id: Number(o.id ?? 0),
    product_id: Number(o.product_id ?? 0),
    product_sku: pickStr(o.product_sku),
    category_id: Number(o.category_id ?? 0),
    pod_selling_price: podPrice,
    swageazy_selling_price: podPrice,
    production_time: pickNum(o.production_time),
    active: asBool(o.active),
    print_on_demand: printOnDemand,
    is_catalogue_product: printOnDemand,
    created_by_admin_id: pickNum(o.created_by_admin_id),
    updated_by_admin_id: pickNum(o.updated_by_admin_id),
    created_date: (o.created_date as Date | string | null) ?? null,
    modified_date: (o.modified_date as Date | string | null) ?? null,
    variants: variantsRaw.map(normalizePodVariant),
  };
}
